// imports
import { Icon } from '@bosch/react-frok'
import type { CSSProperties } from 'react'

// components
import StatusMessage from '@/ui/components/StatusMessage/StatusMessage'

// hooks & utils
import useChatStatusPhases from './useChatStatusPhases'
import useCollapsibleStatus from './useCollapsibleStatus'
import useWindowSize from '@/hooks/useWindowSize'
import type { ChatStatusMessageItem } from './types/ChatStatusMessageTypes'

// styles
import './CollapsibleStatusPanel.scss'

interface ChildRowProps {
    item: ChatStatusMessageItem
    index: number
    showConnectorLine?: boolean
}

// The steps enter one after the other; the delay grows with the index up to this step
const MAX_STAGGER_STEPS = 5

const resolveChildIcon = (state: string) => {
    if (state === 'done') return 'checkmark'
    if (state === 'pending') return 'dot'
    return 'search'
}

const ChildRow = ({ item, index, showConnectorLine = false }: ChildRowProps) => {
    const { isMobile } = useWindowSize()
    const cyclingPhrase = useChatStatusPhases(item.state ?? 'done', item.loadingPhrases ?? [])
    const displayMessage = cyclingPhrase ?? item.message
    const state = item.state ?? 'done'

    return (
        <div
            className={`collapsible-status-panel__children-item -${state}`}
            style={{ '--i': Math.min(index, MAX_STAGGER_STEPS) } as CSSProperties}
        >
            <StatusMessage
                state={state}
                type={item.type}
                visual={item.visual ?? { kind: 'icon', name: resolveChildIcon(state) }}
                message={displayMessage}
                messageSize={item.messageSize ?? (isMobile ? 'small' : 'normal')}
            />
            {item.content && <div className="chat-status-message-item__content">{item.content}</div>}
            {showConnectorLine && <div className="collapsible-status-panel__connector-line" />}
        </div>
    )
}

interface CollapsibleStatusPanelProps {
    item: ChatStatusMessageItem
    showBottomConnector?: boolean
}

const CollapsibleStatusPanel = ({ item, showBottomConnector = false }: CollapsibleStatusPanelProps) => {
    const { isExpanded, toggle } = useCollapsibleStatus(item.state)
    const cyclingPhrase = useChatStatusPhases(item.state ?? 'done', item.loadingPhrases ?? [])
    const hasChildren = !!item.children?.length
    const { isMobile } = useWindowSize()

    const activeChild = item.children?.find(c => c.state === 'loading')
    const headerMessage = item.state === 'loading' && activeChild
        ? activeChild.message
        : (cyclingPhrase ?? item.message)

    return (
        <div className="collapsible-status-panel">
            <button
                className="collapsible-status-panel__header"
                onClick={toggle}
                aria-expanded={isExpanded}
                type="button"
            >
                <StatusMessage
                    state={item.state ?? 'done'}
                    type={item.type}
                    visual={item.visual}
                    message={headerMessage}
                    messageSize={item.messageSize}
                />
                {((isExpanded && hasChildren) || (!isExpanded && showBottomConnector)) && (
                    <div className="collapsible-status-panel__header-connector" />
                )}
                <Icon iconName={isMobile ? "forward-right-small" : "forward-right"} className={`collapsible-status-panel__chevron${isExpanded ? ' --expanded' : ''}`} />
            </button>
            {isExpanded && hasChildren && (
                <div className="collapsible-status-panel__children">
                    {item.children!.map((child, i) => (
                        <ChildRow
                            key={child.id}
                            item={child}
                            index={i}
                            showConnectorLine={i < item.children!.length - 1 || showBottomConnector}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

export default CollapsibleStatusPanel
